import logger from '../logger';

/**
 * Inputs represent by 3, 32bit integer numbers
 *
 * The first inputs 1-16 (input 1 ON = 1, input 8 ON = 64, input 16 ON = 128)
 * The second inputs 17-32 (input 17 ON = 1, input 24 ON = 64, input 32 ON = 128)
 * The third inputs 33-48 (input 33 ON = 1, input 40 ON = 64, input 48 ON = 128)
 *
 * '0,0,0' => '0000000000000000.0000000000000000.0000000000000000'
 *
 */
export const convertInputs = (inputs: string = '0,0,0'): string => {
  const regex = /^-?\d+,-?\d+,-?\d+$/;
  if (!regex.test(inputs)) {
    logger.warn('wrong format of inputs:', inputs);
    return inputs;
  }

  const arr = inputs.split(',').map(v => Number(v));
  // Convert each input integer to a 16-bit binary string
  const inputBin1 = arr[0]?.toString(2).padStart(16, '0').split('').reverse().join('');
  const inputBin2 = arr[1]?.toString(2).padStart(16, '0').split('').reverse().join('');
  const inputBin3 = arr[2]?.toString(2).padStart(16, '0').split('').reverse().join('');

  return `${inputBin1}.${inputBin2}.${inputBin3}`;
};

/**
 * Outputs represent by 2, 32bit integer numbers
 *
 * The first 1-44
 * The second 45-96
 *
 * '0,0' => '0000000000000000.0000000000000000.0000000000000000.0000000000000000.0000000000000000.0000000000000000'
 */
export const convertOutputs = (outputs: string = '0,0'): string => {
  const regex = /^-?\d+,-?\d+$/;
  if (!regex.test(outputs)) {
    logger.warn('wrong format of outputs:', outputs);
    return outputs;
  }

  const arr = outputs.split(',').map(v => Number(v));
  const part1 = arr[0];
  const part2 = arr[1];
  const binary1 = part1.toString(2); // up to 44 bits
  const binary2 = part2.toString(2); // up to 48 bits

  // Concatenate and pad to 96 bits
  const combinedBinary = binary1.padStart(44, '0').split('').reverse().join('') + binary2.padStart(52, '0').split('').reverse().join('');
  const paddedBinary = combinedBinary.padStart(96, '0');

  // Split into 6 groups of 16 bits
  const groups = [];
  for (let i = 0; i < 96; i += 16) {
    groups.push(paddedBinary.slice(i, i + 16));
  }

  return groups.join('.');
};

export const parseInputs = (inputString: string): string => {
  const regex = /^([01]{16}\.){2}[01]{16}$/;
  if (!regex.test(inputString)) {
    logger.warn('wrong format of input:', inputString);
    return inputString;
  }

  const parts = inputString.split('.');

  const reversedNumbers = parts.map(bin => {
    const reversed = bin.split('').reverse().join('');
    return parseInt(reversed, 2); // Assumes unsigned 16-bit integers
  });

  return reversedNumbers.join(',');
};

export const parseOutputs = (outputString: string): string => {
  // Remove dots and get the full binary string
  const fullBinary = outputString.replace(/\./g, '');

  if (fullBinary.length !== 96) {
    logger.warn('Input must contain exactly 96 bits (6 groups of 16 bits).');
    return outputString;
  }

  const binaryString = outputString.split('.').join(''); // Remove dots to get 96-bit string

  // Split the 96-bit string into two parts: first 44 bits and remaining 52 bits
  const reversedBinary1 = binaryString.slice(0, 44).split('').reverse().join('');
  const reversedBinary2 = binaryString.slice(44).split('').reverse().join('');

  // Convert binaries back to numbers
  const part1 = parseInt(reversedBinary1, 2);
  const part2 = parseInt(reversedBinary2, 2);

  return `${part1},${part2}`;
};
